import { useState } from 'react';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from '../../hooks/useCategories';
import { useToast } from '../../components/Toast';
import { Modal, ConfirmDialog } from '../../components/Modal';
import { FormShell, Field, fieldErrorsFromApiError } from '../../components/FormShell';
import { CategoryColorPicker } from '../../components/CategoryColorPicker';
import { CategoryColorSwatch } from '../../components/CategoryColorSwatch';
import type { CategoryDto, UpsertCategoryRequest } from '../../lib/types';

const DEFAULT_FORM: UpsertCategoryRequest = {
  name: '',
  colorHex: '#214592',
  isBillable: false,
  isPtoEquivalent: false,
  sortOrder: 0,
};

export function CategoriesPage() {
  const { data: categories, isLoading, error } = useCategories();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();
  const { success, error: toastError } = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CategoryDto | null>(null);
  const [form, setForm] = useState<UpsertCategoryRequest>(DEFAULT_FORM);
  const [deleteTarget, setDeleteTarget] = useState<CategoryDto | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);

  function openCreate() {
    setEditTarget(null);
    setForm(DEFAULT_FORM);
    setSubmitError(null);
    setModalOpen(true);
  }

  function openEdit(c: CategoryDto) {
    setEditTarget(c);
    setForm({ name: c.name, colorHex: c.colorHex, isBillable: c.isBillable, isPtoEquivalent: c.isPtoEquivalent, sortOrder: c.sortOrder });
    setSubmitError(null);
    setModalOpen(true);
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);
    try {
      if (editTarget) {
        await updateMutation.mutateAsync({ id: editTarget.id, req: form });
        success('Category updated.');
      } else {
        await createMutation.mutateAsync(form);
        success('Category created.');
      }
      setModalOpen(false);
    } catch (err) {
      const msg = (err as Error).message ?? '';
      if (msg.includes('already exists') || msg.includes('409')) {
        toastError('A category with this name already exists.');
      }
      setSubmitError(err);
    }
  }

  async function handleDelete(c: CategoryDto) {
    try {
      await deleteMutation.mutateAsync(c.id);
      success(`Deleted "${c.name}".`);
    } catch (err) {
      const msg = (err as Error).message ?? '';
      if (msg.includes('400') || msg.includes('System')) {
        toastError('System categories cannot be deleted.');
      } else {
        toastError('Could not delete category.');
      }
    }
  }

  const fieldErrors = fieldErrorsFromApiError(submitError);
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-brand-blue">Categories</h1>
          <p className="text-sm text-brand-charcoal/70">Configure how projects are color-coded.</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="bg-brand-blue text-white rounded px-3 py-1.5 text-sm hover:bg-brand-blue/90"
        >
          + New Category
        </button>
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-slate-100 rounded" />)}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600">Failed to load categories.</p>
      )}

      {categories && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">Color</th>
                <th className="py-2 px-3">Billable</th>
                <th className="py-2 px-3">PTO-Equivalent</th>
                <th className="py-2 px-3">System</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 px-3 text-center text-slate-500">
                    No categories configured. Run an ingest or add a category to get started.
                  </td>
                </tr>
              ) : (
                categories.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-medium text-brand-charcoal">{c.name}</td>
                    <td className="py-2 px-3">
                      <CategoryColorSwatch colorHex={c.colorHex} size="md" />
                    </td>
                    <td className="py-2 px-3">{c.isBillable ? 'Yes' : '—'}</td>
                    <td className="py-2 px-3">{c.isPtoEquivalent ? 'Yes' : '—'}</td>
                    <td className="py-2 px-3">
                      {c.isSystem && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">System</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {!c.isSystem && (
                        <>
                          <button
                            type="button"
                            onClick={() => openEdit(c)}
                            className="text-brand-blue text-xs hover:underline mr-3"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(c)}
                            className="text-red-600 text-xs hover:underline"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit category' : 'New category'}
        size="lg"
      >
        <FormShell onSubmit={handleSubmit}>
          <Field
            label="Name"
            name="name"
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            errors={fieldErrors}
          />
          <CategoryColorPicker
            label="Color"
            value={form.colorHex}
            onChange={(hex) => setForm({ ...form, colorHex: hex })}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isBillable}
              onChange={(e) => setForm({ ...form, isBillable: e.target.checked })}
            />
            Count toward billable utilization
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isPtoEquivalent}
              onChange={(e) => setForm({ ...form, isPtoEquivalent: e.target.checked })}
            />
            Treat as PTO stand-in (excluded from billable)
          </label>
          <Field
            label="Display order in legend"
            name="sortOrder"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
          />
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-3 py-1.5 rounded bg-brand-blue text-white hover:bg-brand-blue/90 disabled:opacity-50"
            >
              {isPending ? 'Saving…' : 'Save category'}
            </button>
          </div>
        </FormShell>
      </Modal>

      {deleteTarget && (
        <ConfirmDialog
          open={true}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => { void handleDelete(deleteTarget); setDeleteTarget(null); }}
          title={`Delete "${deleteTarget.name}"?`}
          message={`Delete "${deleteTarget.name}"? Assignments using this category will fall back to Uncategorized.`}
          confirmLabel="Delete"
          destructive
        />
      )}
    </section>
  );
}
